import _ from 'lodash'
import React, { lazy, useEffect, useRef, useState } from 'react'
import { shallowEqual, useSelector, useDispatch } from 'react-redux'
import TextField from './TextField'
import { getValue, option } from '../../../../../../lib/common'
import Autocomplete from '@material-ui/lab/Autocomplete'
import moment from 'moment'
import { KeyboardTimePicker, KeyboardDatePicker, KeyboardDateTimePicker } from './DatePickers'
import clsx from 'clsx'
import { List, isImmutable, fromJS, Map } from 'immutable'
import { TextMaskCustom } from '../TextMaskCustom'
import { strToMask } from '../../../../../../lib/mask'
import SFFieldReference from '../../../../../../components/WidgetBuilder/SFFields/Reference'
import { Checkbox, FormControlLabel, InputAdornment, Radio, RadioGroup, FormLabel } from '@material-ui/core'
import { isSame } from '../../../isSame'
import { RepeatCtx } from '../Grid/GridContext'
import {
  getDateFormat,
  isAmPm,
  getTimeFormat,
  getFormDateFormat,
  getAccessibilityFormDateFormat,
} from '../../../../../../lib/format'
import { wbLoadPreviewCustomScript } from '../../../../../../store/project/wbLoadPreviewCustomScript'
import DateRangeIcon from '../../../../../../components/SVG/DateRangeIcon'
import ClearIcon from '@material-ui/icons/Clear'
import { loadSmartVPickList } from '../../../../../../store/project/actions'
import VisibilityIcon from '@material-ui/icons/Visibility'
import VisibilityOffIcon from '@material-ui/icons/VisibilityOff'
import { getTopModalPosition } from '../../../../../../store/project/lookupFieldOpenModal'
import { SuspenseLoader } from '../../../../../../Web/WebLoader'
import { NumberFormatCustom } from '../NumberFormatCustom'
import numberParsing from 'number-parsing'
import { PhoneInputControl } from './PhoneInput'
import { useId } from 'react'

const FileUploadComponent = lazy(() => import('./FileUploadComponent'))
const SignatureComponent = lazy(() => import('./SignatureComponent'))
const SelectDateRangeModal = lazy(() =>
  import('./SelectDateRangeModal').then((val) => ({ default: val.SelectDateRangeModal }))
)
const AccessibilityDatePicker = lazy(() =>
  import('../AccessibilityComponents/DatePicker').then((module) => ({ default: module.AccessibilityDatePicker }))
)

const toBoolean = (value) =>
  _.toLower(String(value)) === 'true' || _.toLower(String(value)) === 'yes' || _.toLower(String(value)) === 'on'

const DEFAULT_FORM_FIELD_STATUS = fromJS({ status: 'initial' })

const getNumberFormat = (col) => {
  const format = {}

  const value = _.get(col, 'meta.format.value')
  switch (value) {
    case '######.##':
      format.decimalDigits = 2
      format.decimalSymbol = '.'
      break
    case '######.#':
      format.decimalSymbol = '.'
      format.decimalDigits = 1
      break
    case '######,##':
      format.decimalSymbol = ','
      format.decimalDigits = 2
      break
    case '###.###,##':
    case '$###.###,##':
      format.thousandSymbol = '.'
      format.decimalSymbol = ','
      format.decimalDigits = 2
      break
    case '######,#':
      format.decimalDigits = 1
      format.decimalSymbol = ','
      break
    case '###.###,#':
    case '$###.###,#':
      format.thousandSymbol = '.'
      format.decimalDigits = 1
      format.decimalSymbol = ','
      break
    case '###,###':
      format.decimalDigits = 0
      format.thousandSymbol = ','
      break
    case '###,###.##':
    case '$###,###.##':
      format.decimalDigits = 2
      format.thousandSymbol = ','
      format.decimalSymbol = '.'
      break
    case '###,###.#':
    case '$###,###.#':
      format.decimalDigits = 1
      format.thousandSymbol = ','
      format.decimalSymbol = '.'
      break
    case '$###,###':
      format.decimalDigits = 0
      format.thousandSymbol = ','
      format.decimalSymbol = '.'
      break
    case "#'###.##":
      format.thousandSymbol = "'"
      format.decimalSymbol = '.'
      format.decimalDigits = 2
      break
  }

  return format
}

function isDependentValue(index, validFor) {
  try {
    if (_.isArray(validFor)) {
      return validFor.indexOf(index) > -1
    } else {
      var base64 = new sforce.Base64Binary('')
      var decoded = base64.decode(validFor)
      var bits = decoded.charCodeAt(index >> 3)
      return (bits & (0x80 >> index % 8)) != 0
    }
  } catch (err) {
    return false
  }
}

export function RenderFormControl({
  label: labelProvided,
  isMandatory,
  value,
  onChange,
  onBlur: onBlurProvided,
  type,
  field,
  styleKey,
  customMetaChoices,
  isPowerTable,
  mask,
  rulesClasses,
  column,
  PageUID,
  widgetUID,
  row,
  isAutoFocus,
  allowBulkUpdate,
  index,
  isError,
  themeKey = 'FormField',
  id,
  isMultiSelection,
  isRTL,
  uid,
  className,
  isPreview,
  captions,
  component,
  extraClasses = {},
  onOpen,
  filterValue,
  isTableInputFormat,
  stylePrefix = '',
  allowClear,
  customOptions,
  alwaysShrink,
  customVariant,
  tabOrder,
  plainLabel = '',
  ariaLabel = '',
  emitBlurEventPayload = false,
  ...rest
}) {
  const rID = useId()
  const domID = id || rID
  const dispatch = useDispatch()
  const [show, setShow] = useState(false)
  const [top, setTop] = useState(null)
  const [localVal, setLocalVal] = useState(null)
  const [onCloseDate, setOnClose] = useState(null)
  const [open, setOpen] = useState(false)
  const timeRef = useRef(null)
  const dateTimeRef = useRef(null)
  const [topPosition, setTopPosition] = useState(null)
  const dateRef = useRef(null)
  const [isPassVisible, setPassVisible] = useState(false)
  const PageID = useSelector((state) => state.getIn(['page']))

  let metaChoices = useSelector((state) => {
    if (widgetUID) {
      return state.getIn(['meta', 'SmartV-' + widgetUID, field], List())
    } else {
      return state.getIn(['meta', 'SmartV', field], List())
    }
  }, isSame)

  if (
    typeof window !== 'undefined' &&
    isPreview &&
    isTableInputFormat &&
    metaChoices.size === 0 &&
    ['picklist', 'multipicklist'].includes(type)
  ) {
    window.isGetPickList = window.isGetPickList || {}
    if (!window.isGetPickList[`${uid}-${id}`]) {
      window.isGetPickList[`${uid}-${id}`] = true
      dispatch(loadSmartVPickList('PowerTable', uid, PageUID))
    }
  } else if (
    typeof window !== 'undefined' &&
    isPreview &&
    metaChoices.size === 0 &&
    ['picklist', 'multipicklist'].includes(type) &&
    !customOptions
  ) {
    window.isGetPickList = window.isGetPickList || {}
    if (!window.isGetPickList[`${widgetUID}-${id}`]) {
      window.isGetPickList[`${widgetUID}-${id}`] = true
      dispatch(loadSmartVPickList(id, widgetUID, PageUID))
    }
  }

  let dependentParent
  if (column) {
    dependentParent = _.get(column, ['meta', 'dependentParent'])
  }
  const isLoadedSfForce = useSelector((state) => state.getIn(['customScripts', 'isLoadedSForce'], false))
  const isIncludedSfForce = useSelector((state) => state.getIn(['customScripts', 'sfForce'], false))
  const bulkParentValue = useSelector((state) => {
    if (widgetUID && dependentParent) {
      return state.getIn(['state', PageUID, `bulk-${widgetUID}`, dependentParent, index])
    } else {
      return ''
    }
  }, isSame)

  if (dependentParent) {
    if (!isIncludedSfForce) {
      dispatch(wbLoadPreviewCustomScript('sfForce'))
    }
  }
  let metaParentChoices = useSelector((state) => {
    if (!dependentParent) {
      return List()
    }
    if (widgetUID) {
      return state.getIn(['meta', 'SmartV-' + widgetUID, dependentParent], List())
    } else {
      return state.getIn(['meta', 'SmartV', dependentParent], List())
    }
  }, isSame)
  let metaParentChoicesControl = useSelector((state) => {
    if (!dependentParent) {
      return Map()
    }
    if (widgetUID) {
      return state.getIn(['meta', 'SmartV-' + widgetUID, field + '_sfControlValues'], Map())
    } else {
      return state.getIn(['meta', 'SmartV', field + '_sfControlValues'], Map())
    }
  }, isSame)

  let onBlur = onBlurProvided || _.noop
  if (customMetaChoices) {
    metaChoices = customMetaChoices
  }

  let label = isMandatory ? (
    <>
      <span
        className={`ft--theme--${themeKey}--asterix ft--theme--Prj${themeKey}--asterix ft--theme--${stylePrefix}Prj${styleKey}--asterix ft--theme--${stylePrefix}Prj${styleKey}--asterix`}>
        *
      </span>{' '}
      {labelProvided}
    </>
  ) : (
    labelProvided
  )
  const options = React.useMemo(() => {
    if (customOptions) {
      return customOptions
    }
    let localOptions = metaChoices.toJS()
    if (!dependentParent) {
      return localOptions
    }
    let result = []
    if (!isLoadedSfForce) {
      return result
    }

    if (!metaParentChoices.size) {
      return result
    }
    let parentValue = _.get(row, ['original', dependentParent])
    if (allowBulkUpdate && bulkParentValue !== undefined) {
      parentValue = bulkParentValue
    }
    let parentIndex = -1
    let parentOption = _.find(metaParentChoices.toJS(), (opt, index) => {
      if (opt) {
        if (opt.label == parentValue || opt.value == parentValue) {
          parentIndex = index
          return true
        }
      }
    })
    if (!parentOption || parentIndex == -1) {
      return result
    }

    if (metaParentChoicesControl.get(parentOption.value) !== undefined) {
      parentIndex = metaParentChoicesControl.get(parentOption.value)
    }
    result = _.filter(localOptions, (opt) => {
      return isDependentValue(parentIndex, opt.validFor)
    })

    return result
  }, [metaChoices, dependentParent, row, bulkParentValue, metaParentChoices, isLoadedSfForce, customOptions])

  useEffect(
    function () {
      if (onCloseDate) {
        onBlur(localVal)
      }
    },
    [onCloseDate]
  )

  useEffect(
    function () {
      if (isPreview) {
        if (allowBulkUpdate && dependentParent) {
          if (options && options.length) {
            let useValue = getValue(options, value) || getValue(options, value, 'label')
            if (!useValue) {
              onChange({
                target: {
                  value: null,
                },
              })
              onBlur(null)
              return
            }
          }
        }
        if (isMultiSelection) {
          if (value && ['multipicklist', 'picklist'].includes(type)) {
            if (options && options.length) {
              let useValue = getValue(options, value) || getValue(options, value, 'label')
              if (type === 'multipicklist') {
                let val = []
                _.each(String(value).split(';'), (value) => {
                  val.push(getValue(options, value) || getValue(options, value, 'label'))
                })
                useValue = _.compact(val)
              }
              if (allowBulkUpdate && dependentParent && useValue) {
                onChange({
                  target: {
                    value: null,
                  },
                })
              } else if (type === 'multipicklist') {
                let listValue = [],
                  labelValue = []
                _.each(useValue, function (val) {
                  if (val && val.value) {
                    listValue.push(val.value)
                    labelValue.push(val.label)
                  }
                })
                if (value !== listValue.join(';')) {
                  onChange({
                    target: {
                      value: listValue.join(';'),
                      label: labelValue,
                    },
                  })
                }
              } else {
                onChange({
                  target: {
                    value: useValue,
                  },
                })
              }
            }
          }
        }
      }
    },
    [options]
  )

  const additionalKey = String(themeKey).replace('Prj', '')
  let { primary, size, variant } = useSelector((state) => {
    return {
      primary: state.getIn(
        ['project', 'Settings', 'theme', 'lg', themeKey, 'all', 'primary'],
        state.getIn(
          ['project', 'Settings', 'theme', 'lg', themeKey, 'root', 'primary'],
          state.getIn(['project', 'Settings', 'theme', 'lg', additionalKey, 'root', 'primary'])
        )
      ),
      size: state.getIn(
        ['project', 'Settings', 'theme', 'lg', themeKey, 'all', 'sizeVariant'],
        state.getIn(
          ['project', 'Settings', 'theme', 'lg', themeKey, 'root', 'sizeVariant'],
          state.getIn(['project', 'Settings', 'theme', 'lg', additionalKey, 'root', 'sizeVariant'], '')
        )
      ),
      variant:
        customVariant ||
        state.getIn(
          ['project', 'Settings', 'theme', 'lg', themeKey, 'all', 'textVariant'],
          state.getIn(
            ['project', 'Settings', 'theme', 'lg', themeKey, 'root', 'textVariant'],
            state.getIn(['project', 'Settings', 'theme', 'lg', additionalKey, 'root', 'textVariant'], 'standard')
          )
        ),
    }
  }, shallowEqual)

  let originVariant = variant
  if (variant === 'classic') {
    variant = 'outlined'
  }
  if (['standard', 'filled', 'outlined'].indexOf(variant) === -1) {
    variant = 'standard'
  }
  let labelClassName = ''
  let notchedOutlineClass = ''
  let containerClass = ''

  if (originVariant == 'classic') {
    labelClassName = ' ft--shrink ft--classic--label'
    notchedOutlineClass = ' ftnotchedOutline-classic'
    containerClass += 'ft--theme--FormField--ftclassic'
  }

  if (!['medium', 'small'].includes(size)) {
    size = 'medium'
  }

  const fixTimeValue = (val) => {
    if (!val) {
      return null
    }
    let value = new Date()
    let parsed = String(val).split(':')
    value.setHours(parsed[0])
    value.setMinutes(parsed[1])
    if (value === 'Invalid Date') {
      return null
    }
    return value
  }
  const fixDateValue = (val) => {
    if (localVal) {
      return localVal
    }
    if (moment(val).format() == 'Invalid date') {
      if (typeof val == 'object') {
        return val
      } else {
        return null
      }
    } else {
      return moment(val)
    }
  }

  let dateInFormat = 'MM/DD/YYYY'
  if (type == 'date') {
    if (_.get(column, ['meta', 'format', 'dateFormat'])) {
      dateInFormat = _.get(column, ['meta', 'format', 'dateFormat'], 'YYYY-MM-DD')
    } else if (_.get(column, ['meta', 'format', 'value'])) {
      dateInFormat = _.get(column, ['meta', 'format', 'value'], 'YYYY-MM-DD')
    }
    dateInFormat = getDateFormat(dateInFormat)
    if (dateInFormat == 'L') {
      dateInFormat = 'MM/DD/YYYY'
    }
  }

  const prefix = _.get(column, 'meta.prefix', '')
  const suffix = _.get(column, 'meta.postfix', '') || _.get(column, 'meta.suffix', '')

  const accessabilityMode = useSelector((state) => {
    let settings = state.getIn(['project', 'Settings', 'accessabilityToolbar'], Map())
    if (settings.get('enabled') && settings.get('isAccessibilityMode')) {
      return true
    }
    return false
  })

  const startAdornment =
    value && prefix ? (
      <InputAdornment position="start" className="ft--theme--FormField--startado">
        {prefix}
      </InputAdornment>
    ) : null
  const endAdornment = ((value && suffix) || type === 'password' || allowClear) && (
    <InputAdornment position="end" className="ft--theme--FormField--endado">
      {value && suffix}
      {type === 'password' && (
        <>
          {isPassVisible && (
            <VisibilityOffIcon
              style={{ cursor: 'pointer' }}
              onClick={() => setPassVisible(false)}
              className={clsx(`ft--theme--${themeKey}--endIcon`, `ft--theme--${stylePrefix}Prj${styleKey}--endIcon`)}
            />
          )}
          {!isPassVisible && (
            <VisibilityIcon
              style={{ cursor: 'pointer' }}
              onClick={() => setPassVisible(true)}
              className={clsx(`ft--theme--${themeKey}--endIcon`, `ft--theme--${stylePrefix}Prj${styleKey}--endIcon`)}
            />
          )}
        </>
      )}
      {allowClear && (
        <ClearIcon
          className={clsx(
            `ft--theme--${themeKey}--endIcon`,
            `ft--theme--${stylePrefix}Prj${styleKey}--endIcon`,
            'ftclear'
          )}
          onClick={() => {
            onChange({ target: { value: '' } })
            setTimeout(() => {
              if (typeof onBlurProvided === 'function') {
                onBlurProvided('')
              }
            }, 10)
          }}
        />
      )}
    </InputAdornment>
  )

  const isNumericType = ['int', 'number', 'currency'].includes(type)
  const currentType = isNumericType ? 'number' : type === 'password' && isPassVisible ? 'text' : type

  const currentFormat = getNumberFormat(column)
  const separator = currentFormat?.decimalSymbol === ',' ? '.' : ','
  const regex = new RegExp(`\\${separator}`, 'g')

  const normalizedValue = separator && _.isString(value) && currentType === 'number' ? value.replace(regex, '') : value

  const parsedValue = numberParsing(normalizedValue)

  useEffect(function () {
    if (isAutoFocus) {
      setTimeout(function () {
        let domElem = document.getElementById(id)
        if (domElem && typeof domElem.focus == 'function') {
          domElem.focus()
        }
      }, 1000)
    }
  }, [])

  useEffect(function () {
    if (isAutoFocus) {
      setTimeout(function () {
        let domElem = document.getElementById(id)
        if (domElem && typeof domElem.focus == 'function') {
          domElem.focus()
        }
      }, 1000)
    }
  }, [])

  const textEl = (
    <TextField
      variant={variant}
      size={size}
      label={label}
      value={
        ['decimal', 'integer', 'int', 'number', 'currency'].includes(type) && !_.isEmpty(column) ? parsedValue : value
      }
      error={isError === true}
      onChange={onChange}
      autoFocus={isAutoFocus}
      onBlur={(evt) => {
        emitBlurEventPayload ? onBlur(evt) : onBlur()
      }}
      multiline={type === 'textarea' || type === 'richtext'}
      type={
        ['int', 'number', 'currency'].indexOf(type) !== -1
          ? 'number'
          : type === 'password' && isPassVisible
            ? 'text'
            : type
      }
      className={clsx(
        `ft--theme--${stylePrefix}Prj${styleKey}-container`,
        `ft--theme--${stylePrefix}Prj${styleKey}--text`,
        `ft--theme--${stylePrefix}Prj${styleKey}--${variant}`,
        `ft--theme--${themeKey}-container`,
        `ft--theme--${themeKey}--text`,
        `ft--theme--${themeKey}--${variant}`,
        rulesClasses,
        className,
        containerClass
      )}
      FormHelperTextProps={{
        className: 'ft-helper-txt',
        'aria-live': 'assertive',
      }}
      InputLabelProps={{
        ...(alwaysShrink ? { shrink: true } : {}),
        classes: {
          root: `ft--label ${labelClassName}`,
          shrink: 'ft--shrink',
          focused: 'ftfocused',
        },
        for: domID,
        id: `l${domID}`,
      }}
      InputProps={{
        classes: {
          focused: 'ftfocused',
          ...(variant === 'outlined' ? { notchedOutline: 'ftnotchedOutline' + notchedOutlineClass } : {}),
        },
        inputProps: {
          id: domID,
          role: 'textbox',
          ...(ariaLabel || plainLabel ? {} : { ['aria-labelledby']: `l${domID}` }),
          ...(Boolean(mask)
            ? {
                mask: strToMask(mask),
              }
            : {}),
          ...(['decimal', 'integer', 'int', 'number', 'currency'].includes(type) && !_.isEmpty(column)
            ? getNumberFormat(column)
            : {}),
          'aria-label':
            ariaLabel || plainLabel || (typeof labelProvided === 'string' ? labelProvided : rest.placeholder) || '',
          'aria-readonly': rest.readonly || rest.readOnly,
          'aria-disabled': rest.disabled,
          'aria-required': rest.required || isMandatory,
          tabIndex: tabOrder,
        },
        ...(Boolean(mask)
          ? {
              inputComponent: TextMaskCustom,
            }
          : {}),
        ...(['decimal', 'integer', 'int', 'number', 'currency'].includes(type) && !_.isEmpty(column)
          ? {
              inputComponent: NumberFormatCustom,
            }
          : {}),
        endAdornment,
        startAdornment,
      }}
      {...rest}
    />
  )

  switch (type) {
    case 'boolean':
      if (label) {
        return (
          <FormControlLabel
            classes={{
              root: `ft--label`,
              shrink: 'ft--shrink',
              focused: 'ftfocused',
            }}
            onClick={(event, checked) => {
              onChange({
                target: {
                  value: !toBoolean(value),
                },
              })
              onBlur(!toBoolean(value))
            }}
            control={
              <Checkbox
                checked={toBoolean(value)}
                error={isError === true}
                disableTouchRipple
                disableFocusRipple
                disableRipple
                color="inherit"
                size="small"
                inputProps={{
                  role: 'checkbox',
                  ...(ariaLabel || plainLabel
                    ? {}
                    : {
                        ['aria-labelledby']: `l${domID}`,
                      }),
                  'aria-label':
                    ariaLabel ||
                    plainLabel ||
                    (typeof labelProvided === 'string' ? labelProvided : rest.placeholder) ||
                    '',
                  'aria-readonly': rest.readonly || rest.readOnly,
                  'aria-disabled': rest.disabled,
                  'aria-required': rest.required || isMandatory,
                  required: rest.required || isMandatory,
                }}
              />
            }
            label={label}
          />
        )
      } else {
        return (
          <Checkbox
            checked={value === true || value === 'true'}
            onChange={(event, checked) => {
              onChange({
                target: {
                  value: checked,
                },
              })
              onBlur(checked)
            }}
            error={isError === true}
            disableTouchRipple
            disableFocusRipple
            disableRipple
            color="inherit"
            size="small"
          />
        )
      }
      break
    case 'reference':
      let refValue
      if (isImmutable(value)) {
        refValue = value.toJS()
      } else if (value && _.isObject(value)) {
        refValue = value
      } else {
        refValue = {
          ft_text: value,
        }
      }
      let localWidthUId = widgetUID
      if (String(widgetUID).indexOf('SmartVProfile') == -1) {
        localWidthUId = `lookup-${widgetUID}`
      } else {
        PageUID = 'SmartVProfile'
      }
      if (column && _.get(column, ['lookup', 'props', 'action'])) {
        return (
          <SFFieldReference
            value={refValue}
            index={index}
            field={{
              field: {},
              isUpdateable: true,
              widgetID: localWidthUId,
              id: column.id,
              key: column.id,
              onSearch: column.lookup.props,
              props: {
                sf: _.omit(_.get(column, 'lookup.props.sf', {}), ['mapping']),
              },
            }}
            styleKey={styleKey}
            size={size}
            variant={variant}
            rulesClasses={rulesClasses}
            PageUID={PageUID}
            classes={{}}
            elementClasses={`ft--theme--${styleKey}`}
            themeKey={`ft--theme--${themeKey}`}
            label={label}
            containerClass={containerClass}
            notchedOutlineClass={notchedOutlineClass}
            labelClassName={labelClassName}
            onChange={(value) => {
              onChange({
                target: {
                  value: value,
                },
              })
              onBlur(value)
            }}
            disabled={false}
            captions={captions}
          />
        )
      }
      return (
        <TextField
          variant={variant}
          size={size}
          label={label}
          value={_.get(value, ['ft_text'], '')}
          error={isError === true}
          onChange={onChange}
          autoFocus={isAutoFocus}
          onBlur={(evt) => onBlur()}
          multiline={type === 'textarea' || type === 'richtext'}
          type={['int', 'number', 'currency'].indexOf(type) !== -1 ? 'number' : type}
          className={clsx(
            `ft--theme--${stylePrefix}Prj${styleKey}-container`,
            `ft--theme--${stylePrefix}Prj${styleKey}--text`,
            `ft--theme--${stylePrefix}Prj${styleKey}--${variant}`,
            `ft--theme--${themeKey}-container`,
            `ft--theme--${themeKey}--text`,
            `ft--theme--${themeKey}--${variant}`,
            rulesClasses,
            className,
            containerClass
          )}
          FormHelperTextProps={{
            className: 'ft-helper-txt',
          }}
          InputLabelProps={{
            classes: {
              root: `ft--label ${labelClassName}`,
              shrink: 'ft--shrink',
              focused: 'ftfocused',
            },
            for: domID,
            id: `l${domID}`,
          }}
          InputProps={{
            classes: {
              focused: 'ftfocused',
              ...(variant === 'outlined' ? { notchedOutline: 'ftnotchedOutline' + notchedOutlineClass } : {}),
            },
            inputProps: {
              id: domID,
              role: 'textbox',
              ['aria-labelledby']: `l${domID}`,
              ...(Boolean(mask)
                ? {
                    mask: strToMask(mask),
                  }
                : {}),
            },
            ...(Boolean(mask)
              ? {
                  inputComponent: TextMaskCustom,
                }
              : {}),
          }}
          {...rest}
        />
      )
      break
    case 'phone2':
      return (
        <>
          <TextField
            variant={variant}
            size={size}
            label={label}
            value={
              ['decimal', 'integer', 'int', 'number', 'currency'].includes(type) && !_.isEmpty(column)
                ? parsedValue
                : value
            }
            error={isError === true}
            onChange={onChange}
            autoFocus={isAutoFocus}
            onBlur={(evt) => onBlur()}
            multiline={type === 'textarea' || type === 'richtext'}
            type={
              ['int', 'number', 'currency'].indexOf(type) !== -1
                ? 'number'
                : type === 'password' && isPassVisible
                  ? 'text'
                  : type
            }
            className={clsx(
              `ft--theme--${stylePrefix}Prj${styleKey}-container`,
              `ft--theme--${stylePrefix}Prj${styleKey}--text`,
              `ft--theme--${stylePrefix}Prj${styleKey}--${variant}`,
              `ft--theme--${themeKey}-container`,
              `ft--theme--${themeKey}--text`,
              `ft--theme--${themeKey}--${variant}`,
              rulesClasses,
              className,
              containerClass
            )}
            FormHelperTextProps={{
              className: 'ft-helper-txt',
            }}
            InputLabelProps={{
              ...(alwaysShrink ? { shrink: true } : {}),
              classes: {
                root: `ft--label ${labelClassName}`,
                shrink: 'ft--shrink',
                focused: 'ftfocused',
              },
              for: domID,
              id: `l${domID}`,
            }}
            InputProps={{
              classes: {
                focused: 'ftfocused',
                ...(variant === 'outlined' ? { notchedOutline: 'ftnotchedOutline' + notchedOutlineClass } : {}),
              },
              inputProps: {
                id: domID,
                role: 'textbox',
                ['aria-labelledby']: `l${domID}`,
                ...(Boolean(mask)
                  ? {
                      mask: strToMask(mask),
                    }
                  : {}),
                ...(['decimal', 'integer', 'int', 'number', 'currency'].includes(type) && !_.isEmpty(column)
                  ? getNumberFormat(column)
                  : {}),
              },

              inputComponent: PhoneInputControl,

              endAdornment,
              startAdornment,
            }}
            {...rest}
          />
        </>
      )
      break
    case 'phone':
    case 'text':
    case 'textarea':
    case 'email':
    case 'url':
    case 'number':
    case 'currency':
      return textEl
    case 'radio':
      return (
        <>
          <FormLabel
            component="label"
            classes={{
              root: `ft--label ${labelClassName}`,
              shrink: 'ft--shrink',
              focused: 'ftfocused',
            }}>
            {label}
          </FormLabel>
          <RadioGroup
            row={true}
            value={value}
            onChange={(evt) => {
              onChange(evt)
              onBlur(evt.target.value)
            }}>
            {_.map(options, (entry, index) => (
              <FormControlLabel
                value={entry.value}
                key={index}
                control={
                  <Radio
                    readOnly={rest.isDisableFilter || false}
                    classes={{ checked: 'ftchecked' }}
                    disabled={rest.isDisableFilter || false}
                  />
                }
                label={entry.label}
              />
            ))}
          </RadioGroup>
        </>
      )
      break
    case 'multipicklist':
    case 'picklist':
      let control = 'select'
      let useValue = getValue(options, value) || getValue(options, value, 'label')
      if (type === 'multipicklist') {
        control = 'multiSelect'
        let val = []
        _.each(String(value).split(';'), (value) => {
          val.push(getValue(options, value) || getValue(options, value, 'label'))
        })
        useValue = _.compact(val)
      }
      return (
        <Autocomplete
          multiple={type === 'multipicklist'}
          options={options}
          getOptionLabel={(v) => v.label}
          error={isError === true}
          value={useValue}
          style={{ padding: 0 }}
          disableClearable={!allowClear}
          onChange={(event, value) => {
            if (isPowerTable) {
              if (type === 'multipicklist') {
                if (value) {
                  let listValue = [],
                    labelValue = []
                  _.each(value, function (val) {
                    if (val && val.value) {
                      listValue.push(val.value)
                      labelValue.push(val.label)
                    }
                  })
                  onChange({
                    target: {
                      value: listValue.join(';'),
                      label: labelValue,
                    },
                  })
                  onBlur(listValue.join(';'), labelValue)
                } else {
                  onChange({
                    target: {
                      value: value,
                    },
                  })
                  onBlur(value)
                }
              } else {
                onChange({
                  target: {
                    value: value,
                  },
                })
                onBlur(value)
              }
            } else {
              if (type === 'multipicklist') {
                let listValue = [],
                  labelValue = []
                _.each(value, function (val) {
                  if (val && val.value) {
                    listValue.push(val.value)
                    labelValue.push(val.label)
                  }
                })
                onChange({
                  target: {
                    value: listValue.join(';'),
                    label: labelValue,
                  },
                })
                onBlur(listValue.join(';'), labelValue)
              } else {
                onChange({
                  target: {
                    value: value?.value,
                  },
                })
                onBlur(value?.value)
              }
            }
          }}
          onBlur={(event) => {
            if (typeof onBlur === 'function') {
              let parameter = value

              if (isPowerTable) {
                parameter = undefined

                // It is needed to allow saving of empty picklist value when 'Allow Edit' is enabled
                if (type === 'picklist' && !event.target.value) {
                  parameter = null
                }
              }

              onBlur(parameter)
            }
          }}
          classes={{
            paper: clsx(
              `ft--theme--${stylePrefix}Prj${styleKey}--root--menu ft--theme--${themeKey}--root--menu`,
              `ft--theme--${stylePrefix}Prj${styleKey}--picklist--menu`
            ),
            option: clsx(
              `ft--theme--${stylePrefix}Prj${styleKey}--${control} ft--theme--${themeKey}--${control} ft--theme--${themeKey}--picklist--option ft--theme--${themeKey}--picklist--option${control}`,
              `ft--theme--${stylePrefix}Prj${styleKey}--root ft--theme--${themeKey}--root`,
              `ft--theme--${stylePrefix}Prj${styleKey}--root--option ft--theme--${themeKey}--root--option`,
              `ft--theme--${stylePrefix}Prj${styleKey}--picklist--option ft--theme--${stylePrefix}Prj${styleKey}--picklist--option${control}`
            ),
            listbox: `ft--theme--${stylePrefix}Prj${styleKey}--scroll ft--theme--${themeKey}--scroll`,
            inputRoot: 'inputRoot',
            endAdornment: 'ft--theme--FormField--endado',
            clearIndicator: clsx(
              `ft--theme--${themeKey}--endIcon`,
              `ft--theme--${stylePrefix}Prj${styleKey}--endIcon`,
              'ftclear'
            ),
          }}
          className={clsx(
            `ft--theme--${stylePrefix}Prj${styleKey}-container`,
            `ft--theme--${stylePrefix}Prj${styleKey}--${control}`,
            `ft--theme--${stylePrefix}Prj${styleKey}--${variant}`,
            `ft--theme--${themeKey}-container`,
            `ft--theme--${themeKey}--${control}`,
            `ft--theme--${themeKey}--${variant}`,
            rulesClasses,
            className,
            containerClass
          )}
          fullWidth
          renderInput={({ InputProps, ...params }) => (
            <TextField
              {...rest}
              {...params}
              inputProps={{
                ...params.inputProps,
                role: 'textbox',
                ...(ariaLabel || plainLabel
                  ? {}
                  : {
                      ['aria-labelledby']: `l${domID}`,
                    }),
                'aria-label':
                  ariaLabel ||
                  plainLabel ||
                  (typeof labelProvided === 'string' ? labelProvided : rest.placeholder) ||
                  '',
                'aria-readonly': rest.readonly || rest.readOnly,
                'aria-disabled': rest.disabled,
                'aria-required': rest.required || isMandatory,
              }}
              InputProps={{
                ...InputProps,
                id: domID,
                classes: {
                  focused: 'ftfocused',
                  ...(variant === 'outlined' ? { notchedOutline: 'ftnotchedOutline' + notchedOutlineClass } : {}),
                },
                startAdornment: (
                  <>
                    {startAdornment}
                    {InputProps.startAdornment}
                  </>
                ),
                readOnly: rest.isDisableFilter || false,
              }}
              variant={variant}
              size={size}
              label={label}
              value={value}
              fullWidth
              FormHelperTextProps={{
                className: 'ft-helper-txt',
              }}
              InputLabelProps={{
                ...(alwaysShrink ? { shrink: true } : {}),
                classes: {
                  root: `ft--label ${labelClassName}`,
                  shrink: 'ft--shrink',
                  focused: 'ftfocused',
                },
                for: domID,
                id: `l${domID}`,
              }}
            />
          )}
        />
      )
    case 'date':
      if (accessabilityMode) {
        return (
          <AccessibilityDatePicker
            value={new Date(value)}
            placeholder={rest.placeholder}
            label={label}
            uid={column.uid}
            formFieldStatus={DEFAULT_FORM_FIELD_STATUS}
            fieldProps={{ isDisabled: rest.isDisabled }}
            elementClasses={''}
            id={column.uid}
            controlId={`control${column.uid}`}
            onBlur={onBlur}
            disabled={rest.isDisabled}
            readOnly={rest.readOnly}
            tabOrder={tabOrder}
            ariaLabel={ariaLabel || rest.placeholder}
            format={getAccessibilityFormDateFormat({ dateFormat: dateInFormat }, 'date')}
            disableFuture={false}
            disablePast={false}
            variant={variant}
            PageID={PageID}
            onChange={(event) => {
              const date = moment(event.value)
              if (!date) {
                onChange({ target: { value: null } })
                setLocalVal(null)
                // onBlur(null);
                return false
              }
              const localDate = moment(date).format('YYYY-MM-DD')
              if (localDate != 'Invalid date' && date?._isValid) {
                onChange({ target: { value: localDate } })
                setLocalVal(localDate)
                onBlur(localDate)
              } else {
                setLocalVal(date)
              }
            }}
          />
        )
      }

      return (
        <KeyboardDatePicker
          open={open}
          innerRef={dateRef}
          onOpen={() => {
            if (window.self !== window.top) {
              setTopPosition(getTopModalPosition(520))
            }
            if (typeof onOpen === 'function') {
              setOpen(!onOpen())
            } else {
              setOpen(true)
            }
          }}
          onClose={(date, param2) => {
            if (!allowBulkUpdate) {
              setOnClose(true)
            }
            setOpen(false)
          }}
          TextFieldComponent={TextField}
          inputVariant={variant}
          size={size}
          error={isError === true}
          autoOk={!allowBulkUpdate}
          {...(allowBulkUpdate ? { variant: 'dialog' } : {})}
          PopoverProps={{
            classes: {
              paper: `ft--theme--${stylePrefix}Prj${styleKey}--paper ft--theme--${themeKey}--paper ft--theme--${stylePrefix}Prj${styleKey}--date ft--theme--${stylePrefix}Prj${styleKey}--modal ft--theme--${themeKey}--date ft--theme--${themeKey}--modal`,
            },
          }}
          DialogProps={{
            PaperProps: {
              style:
                topPosition !== null
                  ? {
                      display: 'block',
                      left: '50%',
                      top: topPosition,
                      position: 'absolute',
                      transform: 'translateX(-50%)',
                    }
                  : {},
              className: clsx(
                `ft--theme--${stylePrefix}Prj${styleKey}--date ft--theme--${stylePrefix}Prj${styleKey}--modal ft--theme--${themeKey}--date ft--theme--${themeKey}--modal`
              ),
            },
          }}
          label={label}
          value={fixDateValue(value)}
          format={dateInFormat}
          onChange={(date) => {
            if (!date) {
              onChange({ target: { value: null } })
              setLocalVal(null)
              // onBlur(null);
              return false
            }
            const localDate = moment(date).format('YYYY-MM-DD')
            if (localDate != 'Invalid date' && date?._isValid) {
              onChange({ target: { value: localDate } })
              setLocalVal(localDate)
              onBlur(localDate)
            } else {
              setLocalVal(date)
            }
          }}
          {...rest}
          className={clsx(
            `ft--theme--${stylePrefix}Prj${styleKey}-container`,
            `ft--theme--${stylePrefix}Prj${styleKey}--date`,
            `ft--theme--${stylePrefix}Prj${styleKey}--${variant}`,
            `ft--theme--${themeKey}-container`,
            `ft--theme--${themeKey}--date`,
            `ft--theme--${themeKey}--${variant}`,
            rulesClasses,
            className,
            containerClass
          )}
          FormHelperTextProps={{
            className: 'ft-helper-txt',
          }}
          InputProps={{
            classes: {
              focused: 'ftfocused',
              ...(variant === 'outlined' ? { notchedOutline: 'ftnotchedOutline' + notchedOutlineClass } : {}),
            },
            onBlur: (evt) => {
              onBlur(null)
            },
            inputProps: {
              id: domID,
              role: 'textbox',
              ...(ariaLabel || plainLabel
                ? {}
                : {
                    ['aria-labelledby']: `l${domID}`,
                  }),
              'aria-label':
                ariaLabel || plainLabel || (typeof labelProvided === 'string' ? labelProvided : rest.placeholder) || '',
              'aria-readonly': rest.readonly || rest.readOnly,
              'aria-disabled': rest.disabled,
              'aria-required': rest.required || isMandatory,
            },
          }}
          InputLabelProps={{
            classes: {
              root: `ft--label ${labelClassName}`,
              shrink: 'ft--shrink',
              focused: 'ftfocused',
            },
            startAdornment,
            for: domID,
            id: `l${domID}`,
          }}
        />
      )
      break
    case 'time':
      let formatString =
        _.get(column, ['meta', 'format', 'value']) ||
        _.get(column, ['meta', 'timeFormat', 'value']) ||
        _.get(column, ['meta', 'format', 'timeFormat'])
      return (
        <KeyboardTimePicker
          innerRef={timeRef}
          TextFieldComponent={TextField}
          {...(allowBulkUpdate ? { variant: 'dialog' } : {})}
          inputVariant={variant}
          size={size}
          error={isError === true}
          autoOk={!allowBulkUpdate}
          PopoverProps={{
            classes: {
              paper: `ft--theme--${stylePrefix}Prj${styleKey}--paper ft--theme--${themeKey}--paper ft--theme--${stylePrefix}Prj${styleKey}--time ft--theme--${stylePrefix}Prj${styleKey}--modal ft--theme--${themeKey}--time ft--theme--${themeKey}--modal`,
            },
          }}
          DialogProps={{
            PaperProps: {
              style:
                topPosition !== null
                  ? {
                      display: 'block',
                      left: '50%',
                      top: topPosition,
                      position: 'absolute',
                      transform: 'translateX(-50%)',
                    }
                  : {},
              className: clsx(
                `ft--theme--${stylePrefix}Prj${styleKey}--time ft--theme--${stylePrefix}Prj${styleKey}--modal ft--theme--${themeKey}--time ft--theme--${themeKey}--modal`
              ),
            },
          }}
          {...(isTableInputFormat
            ? {
                format: getFormDateFormat(_.get(column, ['meta', 'format'], {}), 'time'),
              }
            : {
                ampm: isAmPm(formatString),
                format: getTimeFormat(formatString),
              })}
          label={label}
          value={fixTimeValue(value)}
          className={clsx(
            `ft--theme--${stylePrefix}Prj${styleKey}-container`,
            `ft--theme--${stylePrefix}Prj${styleKey}--time`,
            `ft--theme--${stylePrefix}Prj${styleKey}--${variant}`,
            `ft--theme--${themeKey}-container`,
            `ft--theme--${themeKey}--time`,
            `ft--theme--${themeKey}--${variant}`,
            rulesClasses,
            className,
            containerClass
          )}
          onChange={(date, param2) => {
            if (allowBulkUpdate) {
              if (date && date.format() != 'Invalid date') {
                onBlur(`${date.format('HH:mm')}:00.000Z`)
              } else if (date && date.format() == 'Invalid date' && date._i && String(date._i).indexOf('_') == -1) {
                onBlur(`${date._i}:00.000Z`)
              } else {
                onBlur(date)
              }
            } else {
              if (date) {
                setLocalVal(`${date.format('HH:mm')}:00.000Z`)
                onChange({ target: { value: `${date.format('HH:mm')}:00.000Z` } })
              } else if (date && date.format() == 'Invalid date' && date._i && String(date._i).indexOf('_') == -1) {
                setLocalVal(`${date._i}:00.000Z`)
                onChange({ target: { value: `${date._i}:00.000Z` } })
              } else {
                setLocalVal(date)
                onChange({ target: { value: date } })
              }
            }
          }}
          open={open}
          onOpen={() => {
            if (window.self !== window.top) {
              setTopPosition(getTopModalPosition(520))
            }
            if (typeof onOpen === 'function') {
              setOpen(!onOpen())
            } else {
              setOpen(true)
            }
          }}
          onClose={(date, param2) => {
            if (!allowBulkUpdate) {
              setOnClose(true)
            }
            setOpen(false)
          }}
          InputProps={{
            classes: {
              focused: 'ftfocused',
              ...(variant === 'outlined' ? { notchedOutline: 'ftnotchedOutline' + notchedOutlineClass } : {}),
            },
            onBlur: (evt) => {
              onBlur(null)
            },
            startAdornment,
            inputProps: {
              id: domID,
              role: 'textbox',
              ...(ariaLabel || plainLabel
                ? {}
                : {
                    ['aria-labelledby']: `l${domID}`,
                  }),
              'aria-label':
                ariaLabel || plainLabel || (typeof labelProvided === 'string' ? labelProvided : rest.placeholder) || '',
              'aria-readonly': rest.readonly || rest.readOnly,
              'aria-disabled': rest.disabled,
              'aria-required': rest.required || isMandatory,
            },
          }}
          FormHelperTextProps={{
            className: 'ft-helper-txt',
          }}
          InputLabelProps={{
            classes: {
              root: `ft--label ${labelClassName}`,
              shrink: 'ft--shrink',
              focused: 'ftfocused',
            },
            for: domID,
            id: `l${domID}`,
          }}
          {...rest}
        />
      )
      break
    case 'datetime':
      const format = isTableInputFormat
        ? getFormDateFormat(_.get(column, ['meta', 'format'], {}), 'datetime')
        : getDateFormat(_.get(column, ['meta', 'format', 'value'], 'MM/DD/YYYY'))

      const isDTAmPm =
        !isTableInputFormat &&
        [
          '8:30 PM',
          '8:30:25 PM',
          'September 4, 1986 8:30 PM',
          'Sep 4, 1986 8:30 PM',
          'Thursday, September 4, 1986 8:30 PM',
          'Thu, Sep 4, 1986 8:30 PM',
        ].includes(_.get(column, ['meta', 'format', 'value'], 'MM/DD/YYYY'))

      return (
        <KeyboardDateTimePicker
          innerRef={dateTimeRef}
          TextFieldComponent={TextField}
          {...(allowBulkUpdate ? { variant: 'dialog' } : {})}
          inputVariant={variant}
          size={size}
          autoOk={!allowBulkUpdate}
          PopoverProps={{
            classes: {
              paper: `ft--theme--${stylePrefix}Prj${styleKey}--paper ft--theme--${themeKey}--paper ft--theme--${stylePrefix}Prj${styleKey}--datetime ft--theme--${stylePrefix}Prj${styleKey}--modal ft--theme--${themeKey}--datetime ft--theme--${themeKey}--modal`,
            },
          }}
          DialogProps={{
            PaperProps: {
              style:
                topPosition !== null
                  ? {
                      display: 'block',
                      left: '50%',
                      top: topPosition,
                      position: 'absolute',
                      transform: 'translateX(-50%)',
                    }
                  : {},
              className: clsx(
                `ft--theme--${stylePrefix}Prj${styleKey}--datetime ft--theme--${stylePrefix}Prj${styleKey}--modal ft--theme--${themeKey}--datetime ft--theme--${themeKey}--modal`
              ),
            },
          }}
          error={isError === true}
          ampm={isTableInputFormat ? _.get(column, ['meta', 'timeFormat', 'value']) === '12' : isDTAmPm}
          format={format}
          label={label}
          value={fixDateValue(value)}
          onChange={(date) => {
            if (allowBulkUpdate) {
              if (date && date.format() != 'Invalid date') {
                onBlur(date)
              } else {
                onBlur(null)
              }
            } else {
              setLocalVal(date)
              onChange({ target: { value: date } })
            }
          }}
          open={open}
          onOpen={() => {
            if (window.self !== window.top) {
              setTopPosition(getTopModalPosition(550))
            }
            if (typeof onOpen === 'function') {
              setOpen(!onOpen())
            } else {
              setOpen(true)
            }
          }}
          onClose={(date, param2) => {
            if (!allowBulkUpdate) {
              setOnClose(true)
            }
            setOpen(false)
          }}
          className={clsx(
            `ft--theme--${stylePrefix}Prj${styleKey}-container`,
            `ft--theme--${stylePrefix}Prj${styleKey}--datetime`,
            `ft--theme--${stylePrefix}Prj${styleKey}--${variant}`,
            `ft--theme--${themeKey}-container`,
            `ft--theme--${themeKey}--datetime`,
            `ft--theme--${themeKey}--${variant}`,
            rulesClasses,
            className,
            containerClass
          )}
          FormHelperTextProps={{
            className: 'ft-helper-txt',
          }}
          InputProps={{
            classes: {
              focused: 'ftfocused',
              ...(variant === 'outlined' ? { notchedOutline: 'ftnotchedOutline' + notchedOutlineClass } : {}),
            },
            onBlur: (evt) => {
              onBlur(null)
            },
            startAdornment,
            inputProps: {
              id: domID,
              role: 'textbox',
              ...(ariaLabel || plainLabel
                ? {}
                : {
                    ['aria-labelledby']: `l${domID}`,
                  }),
              'aria-label':
                ariaLabel || plainLabel || (typeof labelProvided === 'string' ? labelProvided : rest.placeholder) || '',
              'aria-readonly': rest.readonly || rest.readOnly,
              'aria-disabled': rest.disabled,
              'aria-required': rest.required || isMandatory,
            },
          }}
          InputLabelProps={{
            classes: {
              root: `ft--label ${labelClassName}`,
              shrink: 'ft--shrink',
              focused: 'ftfocused',
            },
            for: domID,
            id: `l${domID}`,
          }}
          {...rest}
        />
      )
    case 'TitanLine':
      return <hr className="ft-sf-form-divider" />
    case 'TitanInstructions':
      return (
        <div
          className="ft-sf-form-instr"
          dangerouslySetInnerHTML={{ __html: labelProvided }}
          onClick={(event) => event.stopPropagation()}
        />
      )
    case 'TitanFiles':
      return (
        <RepeatCtx isRepeat={false} isRepeatCol={false} childrenCount={1} index={null} repeatUID={null}>
          <SuspenseLoader>
            <FileUploadComponent
              formFieldStatus={DEFAULT_FORM_FIELD_STATUS}
              uid={uid}
              setFileCustomError={() => undefined}
              fieldProps={{}}
              dragActions={{}}
              onContextMenu={_.noop}
              component={Map.isMap(component) ? component : Map()}
              control="fileupload"
              isPreview={isPreview}
              isRTL={isRTL}
              value={value}
              onChange={onChange}
              PageUID="form"
              extraClasses={extraClasses}
            />
          </SuspenseLoader>
        </RepeatCtx>
      )
    case 'TitanSignature':
      return (
        <RepeatCtx isRepeat={false} isRepeatCol={false} childrenCount={1} index={null} repeatUID={null}>
          <SuspenseLoader>
            <SignatureComponent
              label={label}
              style={{
                height: '250px',
              }}
              formFieldStatus={DEFAULT_FORM_FIELD_STATUS}
              uid={uid}
              dragActions={{}}
              onContextMenu={_.noop}
              component={Map()}
              control="signature"
              isPreview={isPreview}
              isRTL={isRTL}
              value={value}
              onChange={onChange}
              PageUID="form"
            />
          </SuspenseLoader>
        </RepeatCtx>
      )
    case 'daterange':
      return (
        <>
          <TextField
            variant={variant}
            size={size}
            label={label}
            value={value}
            error={isError === true}
            onChange={onChange}
            autoFocus={isAutoFocus}
            onBlur={(evt) => onBlur()}
            multiline={type === 'textarea' || type === 'richtext'}
            type={['int', 'number', 'currency'].indexOf(type) !== -1 ? 'number' : type}
            className={clsx(
              `ft--theme--${stylePrefix}Prj${styleKey}-container`,
              `ft--theme--${stylePrefix}Prj${styleKey}--text`,
              `ft--theme--${stylePrefix}Prj${styleKey}--${variant}`,
              `ft--theme--${themeKey}-container`,
              `ft--theme--${themeKey}--text`,
              `ft--theme--${themeKey}--${variant}`,
              rulesClasses,
              className,
              `ft--theme--${stylePrefix}Prj${styleKey}--daterange`,
              `ft--theme--${themeKey}--daterange`,
              containerClass
            )}
            FormHelperTextProps={{
              className: 'ft-helper-txt',
            }}
            InputLabelProps={{
              classes: {
                root: `ft--label ${labelClassName}`,
                shrink: 'ft--shrink',
                focused: 'ftfocused',
              },
              for: domID,
              id: `l${domID}`,
            }}
            InputProps={{
              classes: {
                focused: 'ftfocused',
                ...(variant === 'outlined' ? { notchedOutline: 'ftnotchedOutline' + notchedOutlineClass } : {}),
              },
              inputProps: {
                id: domID,
                role: 'textbox',
                ...(ariaLabel || plainLabel
                  ? {}
                  : {
                      ['aria-labelledby']: `l${domID}`,
                    }),
                'aria-label':
                  ariaLabel ||
                  plainLabel ||
                  (typeof labelProvided === 'string' ? labelProvided : rest.placeholder) ||
                  '',
                'aria-readonly': rest.readonly || rest.readOnly,
                'aria-disabled': rest.disabled,
                'aria-required': rest.required || isMandatory,
                autoComplete: 'off',
              },
              readOnly: true,
              endAdornment: (
                <InputAdornment position="end" className="ft--theme--FormField--endado">
                  {allowClear && (
                    <ClearIcon
                      className={clsx(
                        `ft--theme--FormField--endIcon`,
                        `ft--theme--${stylePrefix}Prj${styleKey}-endIcon`,
                        `ft--theme--${stylePrefix}Prj${styleKey}--endIcon`,
                        `ft--theme--${stylePrefix}Prj${styleKey}--endIcon`,
                        `ft--theme--${themeKey}--endIcon`,
                        'ftclear'
                      )}
                      onClick={() => onChange({ target: { value: [null, null] } })}
                    />
                  )}
                  <DateRangeIcon
                    style={{ cursor: 'pointer' }}
                    onClick={(event) => {
                      if (!rest.isDisabled) {
                        setTop(getTopModalPosition(700))
                        setShow(true)
                      }
                    }}
                    className={clsx(
                      `ft--theme--FormField--endIcon`,
                      `ft--theme--${stylePrefix}Prj${styleKey}-endIcon`,
                      `ft--theme--${stylePrefix}Prj${styleKey}--endIcon`,
                      `ft--theme--${stylePrefix}Prj${styleKey}--endIcon`,
                      `ft--theme--${themeKey}--endIcon`
                    )}
                  />
                </InputAdornment>
              ),
              startAdornment,
            }}
            {...rest}
          />
          {show && (
            <SuspenseLoader>
              <SelectDateRangeModal
                captions={captions}
                filterValue={filterValue}
                onApply={(range) => {
                  onChange({ target: { value: range } })
                  setShow(false)
                }}
                onClose={() => setShow(false)}
                styleKey={styleKey}
                top={top}
              />
            </SuspenseLoader>
          )}
        </>
      )
    case 'Typography': {
      const tStyle = rest.isFormHeading
        ? {
            display: 'flex',
            justifyContent: 'center',
            width: '100%',
          }
        : {}
      tStyle.wordBreak = 'break-word'
      tStyle.maxWidth = 'calc(100% - 48px)'
      if (rest.textVariant === 'link') {
        return <div className="ft--theme--Link--root ft--Text--base root">{rest.text}</div>
      }
      return (
        <div
          style={tStyle}
          className={`ft--theme--Typography--${rest.textVariant} ft--Text--base root`}
          {...(rest.isHtml ? { dangerouslySetInnerHTML: { __html: rest.text } } : { children: rest.text })}
        />
      )
    }
    case 'Button': {
      return (
        <div style={{ display: 'flex', justifyContent: 'center', width: '100%' }}>
          <div
            className={clsx(`ft--theme--Button--primary`, { disabled: rest.disabled })}
            style={{ alignItems: 'center', justifyContent: 'center', display: 'flex', cursor: 'pointer' }}>
            {rest.text}
          </div>
        </div>
      )
    }
    default:
      return textEl
  }
}
